import { useEffect, useState, type ChangeEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Image as ImageIcon, PlugZap, Save, Trash2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { formatRate, type RestaurantSettings } from '@sebastian/shared';
import { api, ApiError } from '../lib/api';
import { useSettings } from '../hooks/queries';
import { useToast } from '../hooks/useToast';
import {
  Button,
  Card,
  ErrorState,
  Field,
  Input,
  SectionHeading,
  Spinner,
  Textarea,
  Toggle,
} from '../components/ui';
import { ReceiptSheet } from '../components/ReceiptSheet';

/** Percentages are edited as human percentages and stored as basis points. */
const toPercent = (bp: number) => formatRate(bp);
const toBasisPoints = (percent: string) => Math.round(Number(percent) * 100);

const MAX_LOGO_BYTES = 400_000;

/**
 * EasyInvoice connection. It saves on its own (separate endpoint, separate
 * button) because the credentials never travel with the public settings, and
 * the stored password is write-only: leaving the field blank keeps it.
 */
function InvoicingCard() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['settings', 'invoicing'],
    queryFn: api.settings.invoicing,
  });

  const [enabled, setEnabled] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!data) return;
    setEnabled(data.enabled);
    setForm({
      baseUrl: data.baseUrl ?? '',
      username: data.username ?? '',
      password: '',
      taxCode: data.taxCode ?? '',
      pattern: data.pattern ?? '',
      serial: data.serial ?? '',
      defaultUnit: data.defaultUnit,
    });
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.settings.updateInvoicing({ enabled, ...form }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings', 'invoicing'] });
      toast.success(enabled ? 'EasyInvoice saved. Invoices are issued when an order is closed.' : 'EasyInvoice saved (turned off).');
    },
    onError: (caught: unknown) =>
      toast.error(caught instanceof ApiError ? caught.message : 'Could not save the invoicing settings.'),
  });

  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const test = useMutation({
    mutationFn: () => api.settings.testInvoicing(form),
    onMutate: () => setTestResult(null),
    onSuccess: setTestResult,
    onError: (caught: unknown) =>
      setTestResult({
        ok: false,
        message: caught instanceof ApiError ? caught.message : 'The test could not be run.',
      }),
  });

  const set = (key: string) => (event: ChangeEvent<HTMLInputElement>) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  return (
    <Card>
      <SectionHeading
        title="E-invoicing (EasyInvoice)"
        subtitle="Issue an invoice automatically when an order is closed. A provider failure never blocks closing — the invoice can be retried."
        action={
          <Button variant="primary" icon={<Save size={16} />} loading={save.isPending} onClick={() => save.mutate()}>
            Save invoicing
          </Button>
        }
      />
      {isLoading ? (
        <Spinner />
      ) : error ? (
        <p className="text-sm text-danger">Could not load the invoicing settings.</p>
      ) : (
        <div className="space-y-4">
          <Toggle checked={enabled} onChange={setEnabled} label="Issue an EasyInvoice e-invoice when an order is closed" />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Base URL" hint="Production: https://api.easyinvoice.vn. https only.">
              <Input placeholder="https://api.easyinvoice.vn" value={form.baseUrl ?? ''} onChange={set('baseUrl')} />
            </Field>
            <Field label="Tax code">
              <Input value={form.taxCode ?? ''} onChange={set('taxCode')} />
            </Field>
            <Field label="Username">
              <Input autoComplete="off" value={form.username ?? ''} onChange={set('username')} />
            </Field>
            <Field
              label="Password"
              hint={data?.hasPassword ? 'A password is saved. Leave blank to keep it.' : 'Stored encrypted; never shown again.'}
            >
              <Input
                type="password"
                autoComplete="new-password"
                placeholder={data?.hasPassword ? '••••••••' : ''}
                value={form.password ?? ''}
                onChange={set('password')}
              />
            </Field>
            <Field label="Pattern — Mẫu số" hint="Copy the “Mẫu số” column from EasyInvoice’s Danh sách mẫu số ký hiệu, e.g. 1C26MDV.">
              <Input placeholder="1C26MDV" value={form.pattern ?? ''} onChange={set('pattern')} />
            </Field>
            <Field label="Serial — Ký hiệu (optional)" hint="Copy the “Ký hiệu” column. Leave blank if that column is empty in EasyInvoice.">
              <Input value={form.serial ?? ''} onChange={set('serial')} />
            </Field>
            <Field label="Default unit (Đơn vị tính)" hint="Used on invoice lines whose menu item has no unit of its own.">
              <Input placeholder="Phần" value={form.defaultUnit ?? ''} onChange={set('defaultUnit')} />
            </Field>
          </div>
          <p className="text-xs text-ink-faint">
            The service charge is always on the invoice: one “Phí dịch vụ” line per VAT rate, taxed at the rate of the
            food or drink it was charged on.
          </p>
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-line p-3">
            <Button icon={<PlugZap size={16} />} loading={test.isPending} onClick={() => test.mutate()}>
              Test connection
            </Button>
            <p className="min-w-0 flex-1 text-xs text-ink-faint">
              Uses the values above (no need to save first). Nothing is issued or sent to the tax authority — it only
              creates a labelled draft in EasyInvoice that you can delete there.
            </p>
            {testResult ? (
              <p className={testResult.ok ? 'w-full text-sm text-emerald-400' : 'w-full text-sm text-danger'}>
                {testResult.message}
              </p>
            ) : null}
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * Bistrochat bookings. Like EasyInvoice it saves on its own: the token is
 * write-only and never comes back to the browser.
 */
function BookingsCard() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['settings', 'bookings'],
    queryFn: api.settings.bookings,
  });

  const [enabled, setEnabled] = useState(false);
  const [token, setToken] = useState('');
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  useEffect(() => {
    if (data) setEnabled(data.enabled);
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.settings.updateBookings({ enabled, token: token.trim() || undefined }),
    onSuccess: (saved) => {
      setToken('');
      void queryClient.invalidateQueries({ queryKey: ['settings', 'bookings'] });
      void queryClient.invalidateQueries({ queryKey: ['reservations'] });
      toast.success(saved.enabled ? 'Bistrochat connected.' : 'Bistrochat saved (turned off).');
    },
    onError: (caught: unknown) =>
      toast.error(caught instanceof ApiError ? caught.message : 'Could not save the Bistrochat settings.'),
  });

  const test = useMutation({
    mutationFn: () => api.settings.testBookings({ token: token.trim() || undefined }),
    onMutate: () => setTestResult(null),
    onSuccess: setTestResult,
    onError: (caught: unknown) =>
      setTestResult({ ok: false, message: caught instanceof ApiError ? caught.message : 'The test could not be run.' }),
  });

  return (
    <Card>
      <SectionHeading
        title="Bookings (Bistrochat)"
        subtitle="Shows how full each half-hour slot is online, on the Bookings page. The booking book itself is kept here in the POS."
        action={
          <Button variant="primary" icon={<Save size={16} />} loading={save.isPending} onClick={() => save.mutate()}>
            Save bookings
          </Button>
        }
      />
      {isLoading ? (
        <Spinner />
      ) : error ? (
        <p className="text-sm text-danger">Could not load the Bistrochat settings.</p>
      ) : (
        <div className="space-y-4">
          <Toggle checked={enabled} onChange={setEnabled} label="Show Bistrochat availability on the Bookings page" />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="API token"
              hint={
                data?.hasToken
                  ? 'A token is saved. Leave blank to keep it, or paste a new one to replace it.'
                  : 'Starts with “bcv.” — stored encrypted and never shown again.'
              }
            >
              <Input
                type="password"
                autoComplete="off"
                placeholder={data?.hasToken ? '••••••••' : 'bcv.…'}
                value={token}
                onChange={(event) => setToken(event.target.value)}
              />
            </Field>
            <Field label="Venue ID" hint="Read from the token when it is saved.">
              <Input value={data?.venueId ?? '—'} readOnly disabled />
            </Field>
          </div>
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-line p-3">
            <Button icon={<PlugZap size={16} />} loading={test.isPending} onClick={() => test.mutate()}>
              Test connection
            </Button>
            <p className="min-w-0 flex-1 text-xs text-ink-faint">
              Reads today's availability with the token above (or the saved one). Nothing is booked or changed.
            </p>
            {testResult ? (
              <p className={testResult.ok ? 'w-full text-sm text-emerald-400' : 'w-full text-sm text-danger'}>
                {testResult.message}
              </p>
            ) : null}
          </div>
        </div>
      )}
    </Card>
  );
}

export function SettingsPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: settings, isLoading, error, refetch } = useSettings();

  const [form, setForm] = useState<Record<string, string>>({});
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [currencyDecimals, setCurrencyDecimals] = useState(true);

  useEffect(() => {
    if (!settings) return;
    setForm({
      restaurantName: settings.restaurantName,
      addressLine1: settings.addressLine1 ?? '',
      addressLine2: settings.addressLine2 ?? '',
      phone: settings.phone ?? '',
      email: settings.email ?? '',
      website: settings.website ?? '',
      taxNumber: settings.taxNumber ?? '',
      currency: settings.currency,
      currencySymbol: settings.currencySymbol,
      serviceCharge: toPercent(settings.serviceChargeBp),
      foodTax: toPercent(settings.foodTaxBp),
      alcoholTax: toPercent(settings.alcoholTaxBp),
      receiptThankYouMessage: settings.receiptThankYouMessage,
      receiptFooterNote: settings.receiptFooterNote ?? '',
    });
    setLogoUrl(settings.logoUrl);
    setCurrencyDecimals(settings.currencyDecimals);
  }, [settings]);

  const save = useMutation({
    mutationFn: () =>
      api.settings.update({
        restaurantName: form.restaurantName,
        logoUrl,
        addressLine1: form.addressLine1,
        addressLine2: form.addressLine2,
        phone: form.phone,
        email: form.email,
        website: form.website,
        taxNumber: form.taxNumber,
        currency: form.currency?.toUpperCase(),
        currencySymbol: form.currencySymbol,
        currencyDecimals,
        serviceChargeBp: toBasisPoints(form.serviceCharge ?? '0'),
        foodTaxBp: toBasisPoints(form.foodTax ?? '0'),
        alcoholTaxBp: toBasisPoints(form.alcoholTax ?? '0'),
        receiptThankYouMessage: form.receiptThankYouMessage,
        receiptFooterNote: form.receiptFooterNote,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast.success('Settings saved. Orders already open keep their original rates.');
    },
    onError: (caught: unknown) =>
      toast.error(caught instanceof ApiError ? caught.message : 'Could not save settings.'),
  });

  /** The logo is stored inline as a data URL — no object storage needed in v1. */
  function onLogoChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      toast.error('That image is too large. Please use one under 400 KB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setLogoUrl(String(reader.result));
    reader.readAsDataURL(file);
  }

  const set = (key: string) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  if (isLoading) return <Spinner />;
  if (error) {
    return (
      <div className="p-6">
        <ErrorState error={error} onRetry={() => void refetch()} />
      </div>
    );
  }

  const preview: RestaurantSettings | undefined = settings
    ? {
        ...settings,
        restaurantName: form.restaurantName ?? settings.restaurantName,
        logoUrl,
        addressLine1: form.addressLine1 || null,
        addressLine2: form.addressLine2 || null,
        phone: form.phone || null,
        email: form.email || null,
        website: form.website || null,
        taxNumber: form.taxNumber || null,
        currencySymbol: form.currencySymbol ?? settings.currencySymbol,
        currencyDecimals,
        receiptThankYouMessage: form.receiptThankYouMessage ?? settings.receiptThankYouMessage,
        receiptFooterNote: form.receiptFooterNote || null,
      }
    : undefined;

  return (
    <div className="mx-auto max-w-6xl px-5 py-6">
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-wide text-ink">Restaurant Settings</h1>
          <p className="mt-1 text-sm text-ink-faint">
            Branding, fiscal rates and receipt wording. Changes apply to orders opened from now on.
          </p>
        </div>
        <Button variant="primary" icon={<Save size={16} />} loading={save.isPending} onClick={() => save.mutate()}>
          Save changes
        </Button>
      </header>

      <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
        <div className="space-y-6">
          <Card>
            <SectionHeading title="Identity" subtitle="Shown on screen and printed on every receipt." />
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full border border-accent/40 bg-accent/8">
                  {logoUrl ? (
                    <img src={logoUrl} alt="Restaurant logo" className="h-full w-full object-cover" />
                  ) : (
                    <ImageIcon size={24} className="text-accent/60" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="inline-flex h-10 cursor-pointer items-center rounded-lg border border-line px-3 text-sm text-ink-soft transition hover:border-accent/50 hover:text-accent-strong">
                    Upload logo
                    <input type="file" accept="image/*" className="hidden" onChange={onLogoChange} />
                  </label>
                  {logoUrl ? (
                    <button
                      type="button"
                      onClick={() => setLogoUrl(null)}
                      className="inline-flex items-center gap-1 text-xs text-ink-faint transition hover:text-danger"
                    >
                      <Trash2 size={12} /> Remove
                    </button>
                  ) : (
                    <span className="text-xs text-ink-faint">PNG or SVG, under 400 KB.</span>
                  )}
                </div>
              </div>

              <Field label="Restaurant name">
                <Input value={form.restaurantName ?? ''} onChange={set('restaurantName')} />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Address line 1">
                  <Input value={form.addressLine1 ?? ''} onChange={set('addressLine1')} />
                </Field>
                <Field label="Address line 2">
                  <Input value={form.addressLine2 ?? ''} onChange={set('addressLine2')} />
                </Field>
                <Field label="Phone">
                  <Input value={form.phone ?? ''} onChange={set('phone')} />
                </Field>
                <Field label="Email">
                  <Input type="email" value={form.email ?? ''} onChange={set('email')} />
                </Field>
                <Field label="Website">
                  <Input value={form.website ?? ''} onChange={set('website')} />
                </Field>
                <Field label="Tax number">
                  <Input value={form.taxNumber ?? ''} onChange={set('taxNumber')} />
                </Field>
              </div>
            </div>
          </Card>

          <Card>
            <SectionHeading
              title="Money"
              subtitle="Every order stores the rates in force when it was opened, so changing these never rewrites a past bill."
            />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Currency code">
                <Input maxLength={3} value={form.currency ?? ''} onChange={set('currency')} />
              </Field>
              <Field label="Currency symbol">
                <Input maxLength={4} value={form.currencySymbol ?? ''} onChange={set('currencySymbol')} />
              </Field>
              <Field label="Service charge %">
                <Input inputMode="decimal" value={form.serviceCharge ?? ''} onChange={set('serviceCharge')} />
              </Field>
              <Field label="Food / soft drink tax %">
                <Input inputMode="decimal" value={form.foodTax ?? ''} onChange={set('foodTax')} />
              </Field>
              <Field label="Alcohol tax %">
                <Input inputMode="decimal" value={form.alcoholTax ?? ''} onChange={set('alcoholTax')} />
              </Field>
            </div>
            <div className="mt-4">
              <Toggle
                checked={currencyDecimals}
                onChange={setCurrencyDecimals}
                label="Show decimal places (e.g. $25.00 instead of $25) — turn off for currencies like VND"
              />
            </div>
            <p className="mt-3 text-xs text-ink-faint">
              VAT is charged on the service charge too: each tax group's service charge is taxed at that group's rate,
              (amount + service charge) × VAT %.
            </p>
          </Card>

          <InvoicingCard />

          <BookingsCard />

          <Card>
            <SectionHeading title="Receipt wording" />
            <div className="space-y-4">
              <Field label="Thank-you message">
                <Textarea
                  rows={3}
                  value={form.receiptThankYouMessage ?? ''}
                  onChange={set('receiptThankYouMessage')}
                />
              </Field>
              <Field label="Footer note (optional)">
                <Textarea rows={2} value={form.receiptFooterNote ?? ''} onChange={set('receiptFooterNote')} />
              </Field>
            </div>
          </Card>
        </div>

        <aside className="lg:sticky lg:top-6 lg:self-start">
          <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink-faint">
            Receipt preview
          </h2>
          {preview ? (
            <div className="scale-[0.85] origin-top">
              <ReceiptSheet
                receipt={{
                  receiptNumber: '000123',
                  settings: preview,
                  openedAt: new Date().toISOString(),
                  paidAt: new Date().toISOString(),
                  staffName: 'John Alvarez',
                  order: {
                    id: 'preview',
                    guestSource: null,
                    guestStatus: null,
                    tipCents: 0,
                    orderNumber: 1024,
                    tableNumber: 5,
                    tableName: 'Table 5',
                    guestCount: 4,
                    status: 'CLOSED',
                    isInvite: false,
                    openedAt: new Date().toISOString(),
                    closedAt: new Date().toISOString(),
                    grandTotalCents: 13_640,
                    paidCents: 13_640,
                    staffName: 'John Alvarez',
                    paymentMethods: ['CREDIT_CARD'],
                  },
                  lines: [
                    { quantity: 1, name: 'Dry-Aged Ribeye', modifiers: ['Medium Rare'], notes: null, lineSubtotalCents: 5_000 },
                    { quantity: 1, name: 'Pasta Carbonara', modifiers: [], notes: 'No cheese', lineSubtotalCents: 3_000 },
                    { quantity: 1, name: 'Red Wine — Malbec', modifiers: [], notes: null, lineSubtotalCents: 4_000 },
                  ],
                  totals: {
                    subtotalCents: 12_000,
                    discountType: null,
                    discountValue: 0,
                    discountCents: 0,
                    itemDiscountCents: 0,
                    billDiscountCents: 0,
                    serviceChargeBp: toBasisPoints(form.serviceCharge ?? '5'),
                    serviceChargeCents: Math.round((12_000 * toBasisPoints(form.serviceCharge ?? '5')) / 10_000),
                    taxCents:
                      Math.round((8_000 * toBasisPoints(form.foodTax ?? '8')) / 10_000) +
                      Math.round((4_000 * toBasisPoints(form.alcoholTax ?? '10')) / 10_000),
                    taxBreakdown: [
                      {
                        taxType: 'FOOD',
                        rateBp: toBasisPoints(form.foodTax ?? '8'),
                        baseCents: 8_000,
                        taxCents: Math.round((8_000 * toBasisPoints(form.foodTax ?? '8')) / 10_000),
                      },
                      {
                        taxType: 'ALCOHOL',
                        rateBp: toBasisPoints(form.alcoholTax ?? '10'),
                        baseCents: 4_000,
                        taxCents: Math.round((4_000 * toBasisPoints(form.alcoholTax ?? '10')) / 10_000),
                      },
                    ],
                    grandTotalCents: 13_640,
                  },
                  payments: [],
                }}
              />
            </div>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
